import type { HLJSApi, Language } from "highlight.js";

/**
 * highlight.js grammar for RoboQL, transcribed from the canonical ANTLR lexer
 * at `packages/roboql/resources/Roboql.g4` — the grammar the backend actually
 * parses with. Where the two could drift, the `.g4` wins.
 */

/** `And`, `Or` and the result-set constraint tokens. */
export const ROBOQL_KEYWORDS = [
  "AND",
  "OR",
  "SORT",
  "BY",
  "ASC",
  "DESC",
  "LIMIT",
];

/**
 * Spelled-out comparators. The first six also have a symbolic form
 * (`NOT_EQUALS` / `!=`); the rest are word-only.
 */
export const ROBOQL_WORD_OPERATORS = [
  "NOT_EQUALS",
  "EQUALS",
  "GREATER_THAN_OR_EQUAL",
  "GREATER_THAN",
  "LESS_THAN_OR_EQUAL",
  "LESS_THAN",
  "CONTAINS",
  "NOT_CONTAINS",
  "BEGINS_WITH",
  "NOT_LIKE",
  "LIKE",
  "IS_NOT_NULL",
  "IS_NULL",
  "NOT_EXISTS",
  "EXISTS",
];

/** Longest first: the alternation below is tried in order, so `>=` must precede `>`. */
export const ROBOQL_SYMBOLIC_OPERATORS = ["!=", ">=", "<=", "=", ">", "<"];

/** `BooleanLiteral`. */
export const ROBOQL_LITERALS = ["TRUE", "FALSE"];

/** `Wildcard` — a whole-collection query, not a comparator. */
export const ROBOQL_WILDCARD = "*";

// `IdentifierHead` and `IdentifierCharacter` in the `.g4` enumerate the Swift
// identifier ranges. Highlighting does not need that precision, so non-ASCII
// heads are approximated by one coarse range that is a superset of them.
const IDENTIFIER_HEAD = "[A-Za-z_\\u00A8-\\uFFFD]";
const IDENTIFIER_CHARACTER = "[0-9./:A-Za-z_\\u00A8-\\uFFFD]";
// `ArrayLikeIdentifierPart`, e.g. the `[/gps/lat]` of `msgpaths[/gps/lat].mean`.
const ARRAY_LIKE_PART = "\\[[^\\]\\r\\n]*\\]";

const RESERVED_WORDS = [
  ...ROBOQL_KEYWORDS,
  ...ROBOQL_WORD_OPERATORS,
  ...ROBOQL_LITERALS,
]
  .slice()
  .sort((a, b) => b.length - a.length);

// An identifier may contain a reserved word (`is_null_count`, `metadata.exists`),
// so the mode only declines to match when a reserved word stands alone — that is,
// when no identifier character follows it. Without this the identifier mode would
// swallow every keyword, since it matches them too. The leading lookbehind stops
// hljs from sidestepping that veto by starting the match one character later
// (`SORT` would otherwise highlight as the identifier `ORT`).
const IDENTIFIER_RE = new RegExp(
  `(?<![0-9./:A-Za-z_\\u00A8-\\uFFFD[])` +
    `(?!(?:${RESERVED_WORDS.join("|")})(?![0-9./:A-Za-z_[]))` +
    `${IDENTIFIER_HEAD}(?:${IDENTIFIER_CHARACTER}|${ARRAY_LIKE_PART})*`,
);

const SYMBOLIC_OPERATOR_RE = new RegExp(ROBOQL_SYMBOLIC_OPERATORS.join("|"));

// `DecimalLiteral` and `IntLiteral`, both of which carry an optional leading
// minus: `-5` is one token, not an operator followed by a number.
const NUMBER_RE = /-?\d+(?:\.\d+)?/;

export default function roboql(hljs: HLJSApi): Language {
  return {
    name: "RoboQL",
    // `options { caseInsensitive = true; }`
    case_insensitive: true,
    keywords: {
      keyword: ROBOQL_KEYWORDS,
      literal: ROBOQL_LITERALS,
      operator: ROBOQL_WORD_OPERATORS,
    },
    contains: [
      hljs.C_LINE_COMMENT_MODE,
      hljs.C_BLOCK_COMMENT_MODE,
      // The only escapes the `.g4` admits are the quote itself and a backslash.
      {
        scope: "string",
        begin: '"',
        end: '"',
        contains: [{ begin: /\\["\\]/ }],
      },
      {
        scope: "string",
        begin: "'",
        end: "'",
        contains: [{ begin: /\\['\\]/ }],
      },
      { scope: "number", begin: NUMBER_RE, relevance: 0 },
      { scope: "operator", begin: SYMBOLIC_OPERATOR_RE, relevance: 0 },
      { scope: "built_in", begin: /\*/, relevance: 0 },
      { scope: "variable", begin: IDENTIFIER_RE, relevance: 0 },
    ],
  };
}
